import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, ArrowPathIcon, KeyIcon, EyeIcon, EyeSlashIcon, BeakerIcon, ChevronDownIcon, ChevronUpIcon, ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import { formatCurrency, formatDate } from '../../utils/formatters';
import FundPriceModal from './FundPriceModal';
import { fetchStockPrice, hasApiKey, setApiKey } from '../../services/yahooFinance';
import { refreshPricesAndCreateSnapshots } from '../../services/priceRefreshService';
import toast from 'react-hot-toast';

const FundPrices = () => {
  const { fundPrices, pots, portfolios, snapshots, addFundPrice, updateFundPrice, deleteFundPrice, addSnapshot } = useData();
  const [isFundModalVisible, setIsFundModalVisible] = useState(false);
  const [editingFund, setEditingFund] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [expandedFundIds, setExpandedFundIds] = useState(new Set());

  useEffect(() => {
    setApiKeyConfigured(hasApiKey());
  }, []);

  const handleSaveApiKey = () => {
    if (!apiKeyInput.trim()) {
      toast.error('Please enter a valid API key');
      return;
    }
    setApiKey(apiKeyInput.trim());
    setApiKeyConfigured(true);
    setShowApiKeyInput(false);
    setApiKeyInput('');
    toast.success('RapidAPI key saved successfully');
  };

  const handleAddFund = () => {
    if (!hasApiKey()) {
      toast.error('Please configure your RapidAPI key first');
      setShowApiKeyInput(true);
      return;
    }
    setEditingFund(null);
    setIsFundModalVisible(true);
  };

  const handleEditFund = (fund) => {
    setEditingFund(fund);
    setIsFundModalVisible(true);
  };

  const handleDeleteFund = (fundId) => {
    if (window.confirm('Are you sure you want to delete this fund?')) {
      deleteFundPrice(fundId);
    }
  };

  const handleCloseFundModal = () => {
    setEditingFund(null);
    setIsFundModalVisible(false);
  };

  const handleRefreshPrices = async () => {
    const fundsWithSymbols = fundPrices.filter(fund => fund.symbol && fund.symbol.trim() !== '');
    if (fundsWithSymbols.length === 0) {
      toast.error('No funds with symbols to refresh');
      return;
    }

    setIsRefreshing(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const fund of fundsWithSymbols) {
        try {
          const priceData = await fetchStockPrice(fund.symbol);
          updateFundPrice(fund.id, {
            price: priceData.price,
            currency: priceData.currency,
          });
          successCount++;
        } catch (error) {
          console.error(`Failed to refresh price for ${fund.symbol}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully refreshed ${successCount} fund price${successCount !== 1 ? 's' : ''}`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to refresh ${errorCount} fund price${errorCount !== 1 ? 's' : ''}`);
      }
    } catch (error) {
      console.error('Error refreshing prices:', error);
      toast.error('Error refreshing fund prices');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRefreshSingleFund = async (fund) => {
    if (!fund.symbol || fund.symbol.trim() === '') {
      toast.error('No symbol available for this fund');
      return;
    }

    try {
      const priceData = await fetchStockPrice(fund.symbol);
      updateFundPrice(fund.id, {
        price: priceData.price,
        currency: priceData.currency,
      });
      toast.success(`Price updated for ${fund.name}`);
    } catch (error) {
      console.error(`Failed to refresh price for ${fund.symbol}:`, error);
      toast.error(`Failed to update price for ${fund.name}`);
    }
  };

  const handleForceRefreshAndSnapshot = async () => {
    if (!hasApiKey()) {
      toast.error('Please configure your RapidAPI key first');
      setShowApiKeyInput(true);
      return;
    }

    const fundsWithSymbols = fundPrices.filter(fund => fund.symbol && fund.symbol.trim() !== '');
    if (fundsWithSymbols.length === 0) {
      toast.error('No funds with symbols to refresh');
      return;
    }

    setIsTesting(true);

    try {
      console.log('🧪 FORCE REFRESH - Starting price update and snapshot creation...');
      console.log('🧪 Current fund prices:', fundPrices.map(f => ({ name: f.name, price: f.price, lastUpdated: f.lastUpdated })));
      console.log('🧪 Current portfolios:', portfolios.map(p => ({ name: p.name, value: p.currentValue })));

      const results = await refreshPricesAndCreateSnapshots({
        fundPrices,
        portfolios,
        snapshots,
        updateFundPrice,
        addSnapshot,
        silent: false,
        skipSnapshots: false,
        forceRefresh: true, // Bypass the "already updated today" check
      });

      console.log('🧪 FORCE REFRESH - Results:', results);

      if (results.pricesRefreshed && results.priceResults.success > 0) {
        toast.success(`✓ Refreshed ${results.priceResults.success} price(s). Portfolio values will update automatically.`);
      }

      if (results.snapshotsCreated && results.snapshotCount > 0) {
        toast.success(`✓ Created ${results.snapshotCount} snapshot(s)`);
      }

      if (!results.pricesRefreshed && !results.snapshotsCreated) {
        toast.error('No prices or snapshots were updated. Check console for details.');
      }
    } catch (error) {
      console.error('Error in force refresh:', error);
      toast.error('Error during force refresh');
    } finally {
      setIsTesting(false);
    }
  };

  // Get pots using each fund
  const getPotsUsingFund = (fundId) => {
    return pots.filter(pot => pot.fundId === fundId);
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort funds based on sortConfig
  const sortedFunds = [...fundPrices].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Render sort indicator
  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Toggle fund history expansion
  const toggleFundExpansion = (fundId) => {
    setExpandedFundIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fundId)) {
        newSet.delete(fundId);
      } else {
        newSet.add(fundId);
      }
      return newSet;
    });
  };

  // Calculate price change
  const calculatePriceChange = (fund) => {
    if (!fund.previousPrice || fund.previousPrice === fund.price) {
      return null;
    }
    const change = fund.price - fund.previousPrice;
    const percentChange = (change / fund.previousPrice) * 100;
    return { change, percentChange };
  };

  return (
    <div>
      {/* API Key Configuration Section */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <KeyIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-1">
                RapidAPI Configuration
              </h3>
              {apiKeyConfigured ? (
                <p className="text-sm text-gray-600">
                  API key is configured. <button
                    onClick={() => setShowApiKeyInput(!showApiKeyInput)}
                    className="text-blue-600 hover:text-blue-700 font-medium underline"
                  >
                    {showApiKeyInput ? 'Hide' : 'Update key'}
                  </button>
                </p>
              ) : (
                <p className="text-sm text-red-600 font-medium">
                  No API key configured. Please add your RapidAPI key to fetch live prices.
                </p>
              )}
            </div>
          </div>
          {!showApiKeyInput && !apiKeyConfigured && (
            <button
              onClick={() => setShowApiKeyInput(true)}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: '#012E58' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
            >
              Add API Key
            </button>
          )}
        </div>

        {showApiKeyInput && (
          <div className="mt-4 pt-4 border-t border-blue-200">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="Enter your RapidAPI key"
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                >
                  {showApiKey ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
              <button
                onClick={handleSaveApiKey}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                style={{ backgroundColor: '#059669' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#047857'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#059669'}
              >
                Save Key
              </button>
              <button
                onClick={() => {
                  setShowApiKeyInput(false);
                  setApiKeyInput('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-600">
              Get your free API key from{' '}
              <a
                href="https://rapidapi.com/3b-data-3b-data-default/api/yahoo-finance-real-time1"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                RapidAPI Yahoo Finance Real Time
              </a>
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight m-0 mb-2">
            Fund Prices
          </h1>
          <p className="text-sm text-gray-600">
            Manage prices for all your stocks and funds in one place
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleRefreshPrices}
            disabled={isRefreshing || fundPrices.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 text-white rounded-lg font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: isRefreshing ? '#6b7280' : '#059669' }}
            onMouseEnter={(e) => !isRefreshing && (e.currentTarget.style.backgroundColor = '#047857')}
            onMouseLeave={(e) => !isRefreshing && (e.currentTarget.style.backgroundColor = '#059669')}
          >
            <ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>{isRefreshing ? 'Refreshing...' : 'Refresh All'}</span>
          </button>
          <button
            onClick={handleForceRefreshAndSnapshot}
            disabled={isTesting || fundPrices.length === 0}
            className="flex items-center gap-2 px-4 py-2.5 text-white rounded-lg font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: isTesting ? '#6b7280' : '#8b5cf6' }}
            onMouseEnter={(e) => !isTesting && (e.currentTarget.style.backgroundColor = '#7c3aed')}
            onMouseLeave={(e) => !isTesting && (e.currentTarget.style.backgroundColor = '#8b5cf6')}
            title="Force refresh prices and create new snapshots (for testing)"
          >
            <BeakerIcon className={`w-4 h-4 ${isTesting ? 'animate-pulse' : ''}`} />
            <span>{isTesting ? 'Testing...' : 'Test Refresh'}</span>
          </button>
          <button
            onClick={handleAddFund}
            className="flex items-center gap-2 px-5 py-2.5 text-white rounded-lg font-medium transition-colors shadow-sm"
            style={{ backgroundColor: '#012E58' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
          >
            <PlusIcon className="w-5 h-5" />
            <span>Add Fund</span>
          </button>
        </div>
      </div>

      {fundPrices.length === 0 ? (
        <div className="mt-6 text-center py-12 px-6 bg-white rounded-2xl shadow-sm border border-gray-200">
          <p className="text-gray-600 mb-4">
            No funds added yet. Add your first fund to track its price.
          </p>
          <button
            onClick={handleAddFund}
            className="px-5 py-2.5 text-white rounded-lg font-medium transition-colors shadow-sm"
            style={{ backgroundColor: '#012E58' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
          >
            Add Your First Fund
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-1 w-8"></th>
                  <th
                    className="px-4 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('name')}
                  >
                    Fund Name {renderSortIndicator('name')}
                  </th>
                  <th
                    className="px-4 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('symbol')}
                  >
                    Symbol/Ticker {renderSortIndicator('symbol')}
                  </th>
                  <th
                    className="px-4 py-1 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('price')}
                  >
                    Current Price {renderSortIndicator('price')}
                  </th>
                  <th className="px-4 py-1 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Price Change
                  </th>
                  <th
                    className="px-4 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('currency')}
                  >
                    Currency {renderSortIndicator('currency')}
                  </th>
                  <th
                    className="px-4 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('lastUpdated')}
                  >
                    Last Updated {renderSortIndicator('lastUpdated')}
                  </th>
                  <th className="px-4 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Used By
                  </th>
                  <th className="px-4 py-1 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedFunds.map((fund) => {
                  const potsUsingFund = getPotsUsingFund(fund.id);
                  const priceChange = calculatePriceChange(fund);
                  const isExpanded = expandedFundIds.has(fund.id);
                  const hasPriceHistory = fund.priceHistory && fund.priceHistory.length > 0;

                  return (
                    <React.Fragment key={fund.id}>
                      <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-1">
                          {hasPriceHistory && (
                            <button
                              onClick={() => toggleFundExpansion(fund.id)}
                              className="p-1 hover:bg-gray-200 rounded transition-colors"
                              title={isExpanded ? "Hide price history" : "Show price history"}
                            >
                              {isExpanded ? (
                                <ChevronUpIcon className="w-4 h-4 text-gray-600" />
                              ) : (
                                <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                              )}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-1">
                          <span className="text-sm font-semibold text-gray-900">{fund.name}</span>
                        </td>
                        <td className="px-4 py-1">
                          <span className="text-sm text-gray-700">{fund.symbol || '-'}</span>
                        </td>
                        <td className="px-4 py-1 text-right">
                          <span className="text-sm font-bold text-gray-900">
                            {formatCurrency(fund.price, fund.currency, 2)}
                          </span>
                        </td>
                        <td className="px-4 py-1 text-right">
                          {priceChange ? (
                            <div className="flex items-center justify-end gap-1">
                              {priceChange.change > 0 ? (
                                <>
                                  <ArrowTrendingUpIcon className="w-3 h-3 text-green-600" />
                                  <span className="text-xs font-semibold text-green-600">
                                    +{formatCurrency(priceChange.change, fund.currency, 2)}
                                  </span>
                                  <span className="text-xs text-green-600">
                                    ({priceChange.percentChange.toFixed(2)}%)
                                  </span>
                                </>
                              ) : (
                                <>
                                  <ArrowTrendingDownIcon className="w-3 h-3 text-red-600" />
                                  <span className="text-xs font-semibold text-red-600">
                                    {formatCurrency(priceChange.change, fund.currency, 2)}
                                  </span>
                                  <span className="text-xs text-red-600">
                                    ({priceChange.percentChange.toFixed(2)}%)
                                  </span>
                                </>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-1">
                          <span className="inline-flex px-2 py-0.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-full">
                            {fund.currency}
                          </span>
                        </td>
                        <td className="px-4 py-1">
                          <span className="text-sm text-gray-600">
                            {formatDate(fund.lastUpdated)}
                          </span>
                        </td>
                        <td className="px-4 py-1">
                          {potsUsingFund.length > 0 ? (
                            <div className="text-xs text-gray-600">
                              {potsUsingFund.length} pot{potsUsingFund.length !== 1 ? 's' : ''}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">Not used</span>
                          )}
                        </td>
                        <td className="px-4 py-1">
                          <div className="flex items-center justify-center gap-1">
                            {fund.symbol && fund.symbol.trim() !== '' && (
                              <button
                                onClick={() => handleRefreshSingleFund(fund)}
                                className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                                title="Refresh price from Yahoo Finance"
                              >
                                <ArrowPathIcon className="w-4 h-4 text-green-600" />
                              </button>
                            )}
                            <button
                              onClick={() => handleEditFund(fund)}
                              className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                              title="Edit fund"
                            >
                              <PencilIcon className="w-4 h-4 text-gray-600" />
                            </button>
                            <button
                              onClick={() => handleDeleteFund(fund.id)}
                              className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                              title="Delete fund"
                              disabled={potsUsingFund.length > 0}
                            >
                              <TrashIcon
                                className={`w-4 h-4 ${
                                  potsUsingFund.length > 0 ? 'text-gray-300 cursor-not-allowed' : 'text-red-600'
                                }`}
                              />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Price History Row */}
                      {isExpanded && hasPriceHistory && (
                        <tr className="bg-blue-50 border-b border-gray-100">
                          <td colSpan="9" className="px-4 py-3">
                            <div className="max-w-4xl">
                              <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">
                                Price History
                              </h4>
                              <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                                <table className="w-full">
                                  <thead>
                                    <tr className="bg-gray-50">
                                      <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">Date</th>
                                      <th className="px-3 py-1.5 text-right text-xs font-semibold text-gray-600">Price</th>
                                      <th className="px-3 py-1.5 text-right text-xs font-semibold text-gray-600">Change from Previous</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {fund.priceHistory.map((entry, index) => {
                                      const nextEntry = fund.priceHistory[index + 1];
                                      let changeFromPrevious = null;
                                      if (nextEntry) {
                                        const change = entry.price - nextEntry.price;
                                        const percentChange = (change / nextEntry.price) * 100;
                                        changeFromPrevious = { change, percentChange };
                                      }

                                      return (
                                        <tr key={index} className="border-t border-gray-100">
                                          <td className="px-3 py-1.5 text-xs text-gray-700">
                                            {formatDate(entry.date)}
                                          </td>
                                          <td className="px-3 py-1.5 text-right text-xs font-medium text-gray-900">
                                            {formatCurrency(entry.price, entry.currency, 2)}
                                          </td>
                                          <td className="px-3 py-1.5 text-right text-xs">
                                            {changeFromPrevious ? (
                                              <span className={changeFromPrevious.change >= 0 ? 'text-green-600' : 'text-red-600'}>
                                                {changeFromPrevious.change >= 0 ? '+' : ''}
                                                {formatCurrency(changeFromPrevious.change, entry.currency, 2)}
                                                {' '}({changeFromPrevious.percentChange.toFixed(2)}%)
                                              </span>
                                            ) : (
                                              <span className="text-gray-400">-</span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Fund Modal */}
      <FundPriceModal
        visible={isFundModalVisible}
        onCancel={handleCloseFundModal}
        fund={editingFund}
      />
    </div>
  );
};

export default FundPrices;
